// Landing attribution: which link brought this visitor in.
//
// The directory tags every outbound funnel link (`ref=emma-shapiro-us`,
// `ref=david-gasster-us-unsure`, hub CTAs, plus the usual utm_*), and until now
// the widget dropped all of it on the floor — we could see THAT the funnel
// converts but never WHICH profile or campaign did the converting.
//
// Captured once at boot, persisted next to the funnel session, and attached to
// every /public/intake save as `source` (PatientIntake.source, JSONB). It rides
// the intake row rather than the account so abandoned funnels are attributable
// too; account-level queries join through sessionId as usual.
//
// Semantics: LAST touch wins for the keys a new visit actually carries — "which
// link converted them" is the question the directory needs answered — but the
// first-ever ref and landing time are kept under first* so first-touch analysis
// stays possible. A visit with NO tracked params changes nothing.
//
// ⚠️ NO PHI here, ever: this object is attached to analytics-adjacent rows and
// may be queried broadly. Values are allowlisted by key and length-capped.

const STORE_KEY = 'expy.onboarding.attribution';

// Everything else in the query string is ignored.
const TRACKED = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'patient_state', // the state the directory gate was answered with, when present
] as const;

const MAX_VALUE_LENGTH = 128;

export type Attribution = Partial<Record<(typeof TRACKED)[number], string>> & {
  landedAt?: string;
  landingPath?: string;
  firstRef?: string;
  firstLandedAt?: string;
};

function safeGet(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Attribution)
      : null;
  } catch {
    return null;
  }
}

function safeSet(value: Attribution): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(value));
  } catch {
    /* private mode / disabled storage — this visit just goes unattributed */
  }
}

/**
 * Read tracked params off the current URL and fold them into the stored
 * record. Call once at boot, before first render. Returns what's now stored
 * (or null when nothing has ever been captured).
 */
export function captureAttribution(): Attribution | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return safeGet();
  }

  const incoming: Attribution = {};
  for (const key of TRACKED) {
    const value = (params.get(key) || '').trim();
    if (value) incoming[key] = value.slice(0, MAX_VALUE_LENGTH);
  }

  const stored = safeGet();
  if (Object.keys(incoming).length === 0) {
    return stored; // untagged visit: nothing to record, nothing to erase
  }

  const now = new Date().toISOString();
  const merged: Attribution = {
    ...stored,
    ...incoming,
    landedAt: now,
    landingPath: window.location.pathname.slice(0, MAX_VALUE_LENGTH),
    firstRef: stored?.firstRef ?? incoming.ref,
    firstLandedAt: stored?.firstLandedAt ?? now,
  };
  safeSet(merged);

  return merged;
}

/** The stored record, for attaching to intake saves. Never throws. */
export function getAttribution(): Attribution | null {
  return safeGet();
}
